import _ from 'lodash'
import CustomTableUtilMixin from '@/mixins/custom-table-util-mixin'
import TransferDom from '@/directives/transfer-dom'

export default {
  mixins: [
    CustomTableUtilMixin
  ],

  directives: {
    TransferDom
  },

  provide () {
    return {
      tableColumns: this.columns
    }
  },

  props: {
    columns: {
      type: Object,
      default: () => {}
    },

    data: {
      type: Array,
      default: () => []
    },

    loading: {
      type: Boolean,
      default: false
    },

    headerColumn: {
      type: Object,
      required: false
    },

    additionalHeaderColumn: {
      type: Object,
      required: false
    },

    additionalColumns: {
      type: Array,
      required: false,
      default: () => []
    },

    groupBy: {
      type: String,
      required: false
    },

    border: {
      type: Boolean,
      default: false
    },

    stripe: {
      type: Boolean,
      default: false
    },

    scroll: {
      type: Boolean,
      default: false
    },

    noDataText: {
      type: String,
      default: 'No Results'
    },

    indentSize: {
      type: Number,
      default: 10
    },

    maxHeight: {
      type: Number,
      default: 0
    },

    chart: Object,

    styles: {
      type: Object,
      default: () => {}
    },

    valueChangeMap: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      copyOfData: _.cloneDeep(this.data),
      tableHeaderAdditionalLeftColWidth: _.get(this.styles, 'additionalLeftColWidth', 30),
      tableHeaderLeftColWidth: _.get(this.styles, 'leftColWidth', 200),
      tableHeaderRightColWidth: _.get(this.chart, 'width', 150),
      tableColMinWidth: _.get(this.styles, 'colMinWidth', 100),
      tableWidth: 0,
      tableHeight: 0,
      tableHeadHeight: 0,
      tableBodyHeight: 0,
      selectedRow: {},
      selectedColumn: '',
      showTooltip: false,
      tooltipTop: 0,
      tooltipLeft: 0,
      numberTypes: ['number', 'decimal', 'percentage', 'currency', 'currencyDecimal'],
      dateTypes: ['date']
    }
  },

  computed: {
    tableColWidth () {
      const tableHeaderAdditionalLeftColWidth = this.additionalColumns.length ? this.tableHeaderAdditionalLeftColWidth : 0
      const tableHeaderLeftColWidth = this.headerColumn ? this.tableHeaderLeftColWidth : 0
      const tableHeaderRightColWidth = this.chart ? this.tableHeaderRightColWidth : 0
      const width = (this.tableWidth - tableHeaderLeftColWidth - tableHeaderAdditionalLeftColWidth - tableHeaderRightColWidth) / this.tableColNum
      return width < this.tableColMinWidth ? this.tableColMinWidth : width
    },

    tableHeaderRow () {
      if (!this.groupBy || !this.data.length) {
        return []
      }
      const headers = this.data.map(row => row[this.groupBy])
      const occurenceMap = _.countBy(headers)
      return headers.reduce((acc, cur) => acc.includes(cur) ? acc : [...acc, cur], [])
        .map(item => ({ name: item, occurence: occurenceMap[item] }))
    },

    tableBodyBorderPosition () {
      let intermediate = 0
      return this.tableHeaderRow.reduce((acc, cur) => {
        intermediate += cur.occurence
        acc[intermediate] = true
        return acc
      }, {})
    },

    hasTooltip () {
      return _.has(this.columns, [this.selectedColumn, 'tooltip'], false)
    },

    minWidthFactor () {
      const tableHeaderAdditionalLeftColWidth = this.additionalColumns.length ? this.tableHeaderAdditionalLeftColWidth : 0
      const tableHeaderLeftColWidth = this.headerColumn ? this.tableHeaderLeftColWidth : 0
      const tableHeaderRightColWidth = this.chart ? this.tableHeaderRightColWidth : 0
      const accWidth = Object.values(this.columns).reduce((acc, col) => {
        return col.minWidth ? acc + col.minWidth : acc + this.tableColWidth
      }, 0)
      const factor = (this.tableWidth - tableHeaderLeftColWidth - tableHeaderAdditionalLeftColWidth - tableHeaderRightColWidth) / accWidth
      return factor < 1 ? 1 : factor
    }
  },

  methods: {
    /**
     * Util function to check chart is needed
     * @return {Boolean}
     */
    checkChart () {
      return !_.isEmpty(this.chart)
    },

    /**
     * Format cell based on path and format
     * @param {Object} row to find value based on path
     * @param {Array} path The path of the property to get.
     * @param {String} format
     * @param {String} fromFormat Original date format
     * @param {String} toFormat Transformed date format
     * @param {Number} precision Precision for number casting
     * @return {Number|Date|String} return formatted value based on types
     */
    formatCellByPath (row, path, format, fromFormat, toFormat, precision) {
      if (this.numberTypes.includes(format)) {
        return this.formatNumber(_.get(row, path, ''), format, precision)
      } else if (this.dateTypes.includes(format)) {
        return this.$moment(_.get(row, path, new Date()), fromFormat).format(toFormat)
      } else {
        return _.get(row, path, '')
      }
    }
  }
}
